import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { notificationsApi } from '../../../api/notifications.js';
import { ApiError } from '../../../api/client.js';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { t } from '../../../lib/i18n.js';
import {
  VARIABLES, DETAIL_FIELDS, DEFAULT_DESIGN, DEFAULT_SUBJECT,
  renderDesignToHtml, renderDesignToText
} from './emailDesign.js';

function ModeToggle({ mode, onChange }) {
  const btn = (m, label) => (
    <button
      type="button"
      onClick={() => onChange(m)}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${mode === m ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
      {btn('visual', 'Visuale')}
      {btn('html', 'HTML avanzato')}
    </div>
  );
}

function VariablePalette({ onInsert }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <p className="mb-2 text-xs text-ink-muted">Clicca su un campo qui sopra, poi inserisci un dato:</p>
      <div className="flex flex-wrap gap-1.5">
        {VARIABLES.map((v) => (
          <button
            key={v.token}
            type="button"
            onClick={() => onInsert(v.token)}
            className="rounded-full border border-border bg-surface-elevated px-2.5 py-0.5 text-[11px] text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
            title={v.token}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TemplateEditorModal({ open, onClose, template, onSaved }) {
  const isEdit = Boolean(template && template._id);

  const [mode, setMode] = useState('visual');
  const [meta, setMeta] = useState({ key: '', name: '', description: '', enabled: true });
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [htmlRaw, setHtmlRaw] = useState('');
  const [textRaw, setTextRaw] = useState('');
  const [activeField, setActiveField] = useState('subject');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setActiveField('subject');
    if (isEdit) {
      setMeta({ key: template.key, name: template.name, description: template.description || '', enabled: template.enabled });
      setSubject(template.subject || DEFAULT_SUBJECT);
      setHtmlRaw(template.html || '');
      setTextRaw(template.text || '');
      if (template.design) {
        setDesign({ ...DEFAULT_DESIGN, ...template.design });
        setMode('visual');
      } else {
        setDesign(DEFAULT_DESIGN);
        setMode('html');   // raw template: do not clobber the existing HTML
      }
    } else {
      setMeta({ key: '', name: '', description: '', enabled: true });
      setSubject(DEFAULT_SUBJECT);
      setDesign(DEFAULT_DESIGN);
      setHtmlRaw('');
      setTextRaw('');
      setMode('visual');
    }
  }, [open, template, isEdit]);

  const setDesignField = (k, v) => setDesign((d) => ({ ...d, [k]: v }));
  const setButtonField = (k, v) => setDesign((d) => ({ ...d, button: { ...(d.button || {}), [k]: v } }));

  const toggleDetail = (key) => setDesign((d) => {
    const has = (d.detailFields || []).includes(key);
    const next = has
      ? d.detailFields.filter((x) => x !== key)
      : DETAIL_FIELDS.map((f) => f.key).filter((k2) => k2 === key || (d.detailFields || []).includes(k2));
    return { ...d, detailFields: next };
  });

  const appendToActive = (token) => {
    const add = (s) => `${s && !String(s).endsWith(' ') ? `${s} ` : (s || '')}${token}`;
    switch (activeField) {
      case 'heading': setDesignField('heading', add(design.heading)); break;
      case 'intro': setDesignField('intro', add(design.intro)); break;
      case 'footer': setDesignField('footer', add(design.footer)); break;
      case 'buttonLabel': setButtonField('label', add(design.button?.label || '')); break;
      default: setSubject(add(subject));
    }
  };

  const currentHtml = () => (mode === 'visual' ? renderDesignToHtml(design) : htmlRaw);
  const currentText = () => (mode === 'visual' ? renderDesignToText(design) : (textRaw || null));

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        name: meta.name,
        description: meta.description,
        subject,
        html: currentHtml(),
        text: currentText(),
        enabled: meta.enabled,
        design: mode === 'visual' ? design : null
      };
      return isEdit ? notificationsApi.updateTemplate(template._id, body) : notificationsApi.createTemplate({ ...body, key: meta.key });
    },
    onSuccess: () => { toast.success('Template salvato.'); onSaved(); onClose(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const previewMutation = useMutation({
    mutationFn: () => notificationsApi.previewTemplate({ subject, html: currentHtml(), text: currentText() }),
    onSuccess: (data) => setPreview(data),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const hasButton = Boolean(design.button);
  const canSave = meta.name && subject && (mode === 'visual' || htmlRaw) && (isEdit || meta.key);

  const fieldFocus = (name) => ({ onFocus: () => setActiveField(name) });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifica "${template?.name}"` : 'Nuovo template'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button variant="secondary" onClick={() => previewMutation.mutate()} loading={previewMutation.isPending} disabled={!subject}>Anteprima</Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!canSave}>Salva</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!isEdit && <Input label="Chiave (univoca)" placeholder="es. avviso-sicurezza" value={meta.key} onChange={(e) => setMeta((m) => ({ ...m, key: e.target.value }))} hint="Identificativo interno: minuscole, numeri e trattini." />}
          <Input label="Nome" value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} />
          <div className={isEdit ? 'sm:col-span-2' : ''}>
            <Input label="Descrizione" value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Contenuto</span>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>

        <Input label="Oggetto" value={subject} onChange={(e) => setSubject(e.target.value)} {...fieldFocus('subject')} placeholder={DEFAULT_SUBJECT} />

        {mode === 'visual' ? (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border bg-surface p-3">
              <div>
                <p className="text-sm font-medium text-ink">Mostra il logo in alto</p>
                <p className="text-xs text-ink-muted">Il logo si imposta in Configurazione (URL logo).</p>
              </div>
              <Switch checked={design.showLogo !== false} onChange={(v) => setDesignField('showLogo', v)} />
            </div>
            <Input label="Titolo" value={design.heading} onChange={(e) => setDesignField('heading', e.target.value)} {...fieldFocus('heading')} />
            <Textarea label="Testo introduttivo" rows={3} value={design.intro} onChange={(e) => setDesignField('intro', e.target.value)} {...fieldFocus('intro')} />

            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">Mostra i dettagli dell'evento</p>
                  <p className="text-xs text-ink-muted">Una tabella con i dati selezionati qui sotto.</p>
                </div>
                <Switch checked={design.showDetails} onChange={(v) => setDesignField('showDetails', v)} />
              </div>
              {design.showDetails && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {DETAIL_FIELDS.map((f) => {
                    const on = (design.detailFields || []).includes(f.key);
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => toggleDetail(f.key)}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? 'border-ink bg-ink text-white' : 'border-border text-ink-muted hover:text-ink'}`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">Pulsante</p>
                  <p className="text-xs text-ink-muted">Un bottone che porta a una pagina.</p>
                </div>
                <Switch checked={hasButton} onChange={(v) => setDesignField('button', v ? { label: 'Apri il pannello', url: '{{app.baseUrl}}/admin/logs' } : null)} />
              </div>
              {hasButton && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input label="Testo del pulsante" value={design.button.label || ''} onChange={(e) => setButtonField('label', e.target.value)} {...fieldFocus('buttonLabel')} />
                  <Input label="Indirizzo (link)" value={design.button.url || ''} onChange={(e) => setButtonField('url', e.target.value)} />
                </div>
              )}
            </div>

            <Input label="Nota a piè di pagina" value={design.footer} onChange={(e) => setDesignField('footer', e.target.value)} {...fieldFocus('footer')} />

            <VariablePalette onInsert={appendToActive} />
          </>
        ) : (
          <>
            <Textarea label="Corpo HTML" rows={12} value={htmlRaw} onChange={(e) => setHtmlRaw(e.target.value)} />
            <Textarea label="Corpo testo (opzionale)" rows={3} value={textRaw} onChange={(e) => setTextRaw(e.target.value)} />
          </>
        )}

        <div className="flex items-center justify-between rounded-lg border border-border bg-surface p-3">
          <span className="text-sm font-medium text-ink">Template attivo</span>
          <Switch checked={meta.enabled} onChange={(v) => setMeta((m) => ({ ...m, enabled: v }))} />
        </div>

        {preview && (
          <div className="rounded-lg border border-border">
            <div className="border-b border-border bg-surface px-3 py-2 text-xs text-ink-muted">Oggetto: <span className="text-ink">{preview.subject}</span></div>
            <iframe title="Anteprima email" className="h-72 w-full rounded-b-lg bg-white" sandbox="" srcDoc={preview.html} />
          </div>
        )}
      </div>
    </Modal>
  );
}
